import { wsConnected, wsDisconnected, wsError, wsMessage } from "../redux/slices/stream";
import { Auth } from "aws-amplify";
import aws4 from "aws4";
import {API_REGION, SOCKET_HOST} from "../redux/slices/auth";

export const createWebSocketMiddleware = () => {
    let socket = null;
    let site = null;
    let reconnectTimer = null;
    let lastUrl = null;
    let manualClose = false; // prevents reconnection when using ws/stop

    async function getSignedWebSocketUrl(api) {
        try {

            if (await Auth.currentCredentials() == null) {
                throw new Error("No current user creds");
            }
            await Auth.currentSession();
            const creds = await Auth.currentCredentials();

            // region = api.site?.config?.awsRegion ?? API_REGION;

            return signWebSocketUrl({
                host: SOCKET_HOST,
                stage: process.env.REACT_APP_SOCKET_STAGE || "prod",
                API_REGION,
                credentials: creds,
            });
        } catch (error) {
            console.error("Error getting AWS credentials for WebSocket:", error);
            //log out
            //await Auth.signOut();

        }
    }

    function signWebSocketUrl({ host, stage, region, credentials }) {
        const { accessKeyId, secretAccessKey, sessionToken } = credentials;
        const path = `/${stage}`;

        const reqToSign = {
            host,
            path,
            service: "execute-api",
            region,
            method: "GET",
            signQuery: true,
            headers: {},
        };

        aws4.sign(reqToSign, { accessKeyId, secretAccessKey, sessionToken });

        let signedPath = reqToSign.path;

        if (!signedPath.includes("X-Amz-Security-Token")) {
            const sep = signedPath.includes("?") ? "&" : "?";
            signedPath += `${sep}X-Amz-Security-Token=${encodeURIComponent(sessionToken)}`;
        }

        return `wss://${host}${signedPath}`;
    }

    function scheduleReconnect(store, attempts = 0) {
        if (manualClose) return;
        clearTimeout(reconnectTimer);
        const delay = Math.min(2000 * Math.pow(1.5, attempts), 30000); // Max 30 seconds
        reconnectTimer = setTimeout(async () => {
            if (manualClose || (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING))) return;
            try {
                const newUrl = await getSignedWebSocketUrl();
                if (newUrl) {
                    connect(store, newUrl);
                } else {
                    scheduleReconnect(store, attempts + 1);
                }
            } catch (e) {
                console.error("Failed to reconnect (signing failed)", e);
                scheduleReconnect(store, attempts + 1);
            }
        }, delay);
    }

    function connect(store, url) {
        if(!url) {
            console.error("WebSocket URL is required to connect");
            return;
        }
        console.log("Connecting to WebSocket URL:", url);
        if (socket && socket.readyState === WebSocket.OPEN) return;
        if (socket && socket.readyState === WebSocket.CONNECTING) return;

        manualClose = false;
        lastUrl = url;


        socket = new WebSocket(url);

        socket.onopen = () => {
            clearTimeout(reconnectTimer);
            store.dispatch(wsConnected());
        };

        socket.onclose = () => {
            store.dispatch(wsDisconnected());
            socket = null;

            if (!manualClose) {
                scheduleReconnect(store, 0);
            }
        };

        socket.onerror = (err) => {
            console.error("WebSocket error:", err);
            store.dispatch(wsError(err));
        };

        socket.onmessage = (msg) => {
            // Stream processing paused (e.g. asset dialog open) - drop the message
            // to avoid re-rendering the dashboard behind the modal. Consumers
            // resync via a full refetch when the stream resumes.
            if (store.getState().stream?.pauseCount > 0) return;
            let data = msg.data;
            try { data = JSON.parse(msg.data); } catch {}
            store.dispatch(wsMessage(data));
        };
    }

    const handleReactivate = async () => {
        if (!manualClose && (!socket || socket.readyState === WebSocket.CLOSED)) {
            try {
                // Fetch fresh signed URL to avoid AWS credentials expiring during background time
                const url = await getSignedWebSocketUrl();
                if (url && window._reduxStore) connect(window._reduxStore, url);
            } catch (e) {
                console.error("Reactivation reconnect failed:", e);
                if (window._reduxStore) scheduleReconnect(window._reduxStore, 0);
            }
        }
    };

    // Listen for tab visibility changes
    if (typeof document !== "undefined") {
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "visible") handleReactivate();
        });
    }

    // Listen for window focus events (especially for mobile iOS)
    if (typeof window !== "undefined") {
        window.addEventListener("focus", handleReactivate);
    }

    return (store) => {
        // store reference so visibility handler can access it
        window._reduxStore = store;

        return (next) => async (action) => {
            switch (action.type) {
                case "ws/start":
                    if (!socket) {
                        try {
                            const signedUrl = await getSignedWebSocketUrl();
                            connect(store, signedUrl);
                        } catch (error) {
                            store.dispatch(wsError("Could not create signed WebSocket URL"));
                        }
                    }
                    break;

                case "ws/stop":
                    manualClose = true;
                    if (socket) {
                        socket.close();
                        socket = null;
                    }
                    clearTimeout(reconnectTimer);
                    break;

                case "ws/send":
                    if (socket && socket.readyState === WebSocket.OPEN) {
                        socket.send(JSON.stringify(action.payload));
                    }
                    break;
            }

            return next(action);
        };
    };
};
